import React, { useEffect, useState } from "react";
import { RefreshCw, CalendarRange } from "lucide-react";
import { Layout } from "../../../layouts/DashboardLayout";
import { useAmbassadors } from "./hooks/useAmbassadors";
import { AmbassadorFilters } from "./components/AmbassadorFilters";
import { AmbassadorChart } from "./components/AmbassadorChart";
import { AmbassadorsTab } from "./components/AmbassadorsTab";
import { AmbassadorPeriod } from "../../../services/ambassadorService";

const PERIOD_OPTIONS: { value: AmbassadorPeriod; label: string }[] = [
  { value: "", label: "Depuis le début" },
  { value: "day", label: "Aujourd'hui (24h)" },
  { value: "2weeks", label: "2 semaines" },
  { value: "month", label: "Mois (30 j)" },
  { value: "quarter", label: "Trimestre (90 j)" },
  { value: "semester", label: "Semestre (180 j)" },
  { value: "year", label: "Année (365 j)" },
];

const AmbassadorsPage: React.FC = () => {
  const {
    ambassadors,
    total,
    page,
    pageSize,
    filters,
    summary,
    monthlyStats,
    selectedYear,
    period,
    startDate,
    endDate,
    loading,
    statsLoading,
    error,
    loadAmbassadors,
    loadMonthlyStats,
    updateFilter,
    resetFilters,
    applyFilters,
    changePage,
    changeYear,
    changePeriod,
    changeDateRange,
    updateAmbassador,
  } = useAmbassadors(50);

  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  useEffect(() => {
    loadAmbassadors(0);
    loadMonthlyStats(selectedYear);
  }, []);

  const handleReset = () => {
    resetFilters();
  };

  return (
    <Layout>
      <div className="p-6 space-y-6 min-h-screen bg-gray-50">
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Ambassadeurs</h1>
            <p className="text-sm text-gray-500">Suivi des parrainages et performance des ambassadeurs</p>
          </div>
          <button
            onClick={() => {
              loadAmbassadors(page);
              loadMonthlyStats(selectedYear);
            }}
            disabled={loading || statsLoading}
            className="p-2 text-gray-600 bg-white rounded-lg border hover:bg-gray-50"
          >
            <RefreshCw size={16} className={loading || statsLoading ? "animate-spin" : ""} />
          </button>
        </div>

        <AmbassadorFilters
          filters={filters}
          onFilterChange={updateFilter}
          onApply={applyFilters}
          onReset={handleReset}
          loading={loading}
        />

        <div className="p-4 bg-white rounded-xl border">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2 text-gray-700">
              <CalendarRange size={16} className="text-green-600" />
              <span className="text-sm font-medium">Période des statistiques</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {PERIOD_OPTIONS.map((opt) => (
                <button
                  key={opt.value || "all"}
                  onClick={() => {
                    setCustomStart("");
                    setCustomEnd("");
                    changePeriod(opt.value);
                  }}
                  disabled={loading}
                  className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
                    period === opt.value && !startDate && !endDate
                      ? "bg-green-600 text-white border-green-600"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2 items-center ml-auto">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="px-2 py-1.5 text-xs rounded-lg border"
              />
              <span className="text-xs text-gray-400">→</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="px-2 py-1.5 text-xs rounded-lg border"
              />
              <button
                onClick={() => {
                  if (customStart || customEnd) {
                    changeDateRange(customStart, customEnd);
                  }
                }}
                disabled={loading || (!customStart && !customEnd)}
                className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-40"
              >
                Appliquer
              </button>
            </div>
          </div>
          {(startDate || endDate || period) && (
            <p className="mt-2 text-xs text-gray-500">
              Les invités, actifs, volumes, bonus, commissions, gadgets et salaires affichés correspondent
              uniquement à la période sélectionnée. Les coûts de bonus sont figés aux taux en vigueur au
              moment de chaque activation.
            </p>
          )}
        </div>

        <AmbassadorChart
          stats={monthlyStats}
          selectedYear={selectedYear}
          availableYears={monthlyStats?.available_years || [selectedYear]}
          loading={statsLoading}
          onYearChange={changeYear}
        />

        <AmbassadorsTab
          ambassadors={ambassadors}
          total={total}
          page={page}
          pageSize={pageSize}
          summary={summary}
          loading={loading}
          error={error}
          onRefresh={() => loadAmbassadors(page)}
          onPageChange={changePage}
          onUpdate={updateAmbassador}
        />
      </div>
    </Layout>
  );
};

export default AmbassadorsPage;
